import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { getActiveDineOutletId } from 'auth/dineOutlet';
import type { DineOutletItem, DineOutletStation } from 'api/types';

/**
 * One dish as the header outlet sells it (DO-312, BR-062): its price here,
 * a department here, sold out here and on sale. The dish itself (name, tax,
 * group) is edited once on Menu Items for every outlet.
 */
interface Props {
  isOpen: boolean;
  record: DineOutletItem | null;
  outletName: string;
  onClose: () => void;
  onSaved: () => void;
}

interface FormData { price: string; stationId: string; isSoldOut: boolean; isActive: boolean; displayOrder: string }

const DineOpsOutletItemEditModal: React.FC<Props> = ({ isOpen, record, outletName, onClose, onSaved }) => {
  const [formData, setFormData] = useState<FormData>({ price: '', stationId: '', isSoldOut: false, isActive: true, displayOrder: '' });
  const initialSnapshotRef = useRef('');
  const recordRef = useRef(record);
  recordRef.current = record;
  const [stations, setStations] = useState<DineOutletStation[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen || !recordRef.current) return;
    const r = recordRef.current;
    const data: FormData = {
      price: r.price == null ? '' : String(r.price),
      stationId: r.stationId ?? '',
      isSoldOut: r.isSoldOutHere,
      isActive: r.isActive,
      displayOrder: r.displayOrder == null ? '' : String(r.displayOrder),
    };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setServerError(null);
    const outletId = getActiveDineOutletId();
    if (outletId) {
      dineOpsService.getOutletStations(outletId)
        .then((list) => setStations(list.filter((s) => s.isAllowed)))
        .catch(() => setStations([]));
    }
  }, [isOpen]);

  const isDirty = useMemo(
    () => !!initialSnapshotRef.current && JSON.stringify(formData) !== initialSnapshotRef.current,
    [formData],
  );

  const set = (key: keyof FormData, value: string | boolean) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (formData.price.trim() !== '' && !(Number(formData.price) >= 0)) errors.price = 'Enter a price of 0 or more, or leave it empty for the menu price.';
    if (formData.displayOrder.trim() !== '' && !Number.isInteger(Number(formData.displayOrder))) errors.displayOrder = 'A whole number.';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    if (!record?.id) return;
    setSaveConfirmOpen(false);
    setIsSaving(true);
    setServerError(null);
    try {
      await dineOpsService.updateOutletItem(record.id, {
        price: formData.price.trim() === '' ? null : Number(formData.price),
        stationId: formData.stationId || null,
        isSoldOut: formData.isSoldOut,
        isActive: formData.isActive,
        displayOrder: formData.displayOrder.trim() === '' ? null : Number(formData.displayOrder),
        rowVersion: record.rowVersion,
      });
      notificationService.toast.success(`"${record.itemName}" updated for ${outletName}.`);
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      setServerError(err?.response?.data?.message ?? 'That could not be saved.');
    }
  }, [record, formData, outletName, onSaved, onClose]);

  if (!isOpen || !record) return null;
  const isSize = !!record.parentItemId;
  const hasSizes = (record.sizeCount ?? 0) > 0;

  const err = (k: string) => fieldErrors[k];

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto', maxWidth: 620 }}>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{record.itemName} — {outletName}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {serverError && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
            {record.courseName} · {record.groupName} · menu price {record.isOpenPrice ? 'open' : Number(record.basePrice).toFixed(2)} ·
            goes to {record.effectiveStationName ?? '—'}. Name, tax and group are changed on Menu Items, for every outlet.
            {isSize && ' This is a size of its dish: it goes to the dish\'s kitchen station.'}
            {hasSizes && ' This dish is sold by size: set each size\'s price on its own row.'}
          </p>
          <div className="form-grid-2">
            <div className="form-group" data-field="price">
              <label className="form-label" htmlFor="oi-price">Price here</label>
              <input id="oi-price" type="number" min={0} step="0.01" className={`form-input${err('price') ? ' form-field-error' : ''}`}
                value={formData.price} onChange={(e) => set('price', e.target.value)} disabled={record.isOpenPrice || hasSizes}
                placeholder={record.isOpenPrice ? 'Entered at order time' : hasSizes ? 'Priced by size' : `Menu price ${Number(record.basePrice).toFixed(2)}`} />
              {err('price') && <div className="form-error-message">{err('price')}</div>}
            </div>
            {!isSize && (
            <div className="form-group" data-field="stationId">
              <SyutSelect label="Kitchen station here" options={stations.map((s) => ({ value: s.stationId, label: s.stationName }))}
                value={formData.stationId} onChange={(v) => set('stationId', (v as string) || '')} placeholder="As the dish says"
                size="sm" isClearable menuPortalTarget={document.body} />
            </div>
            )}
          </div>
          <div className="form-grid-2">
            <div className="form-group" data-field="displayOrder">
              <label className="form-label" htmlFor="oi-order">Position on this card</label>
              <input id="oi-order" type="number" className={`form-input${err('displayOrder') ? ' form-field-error' : ''}`}
                value={formData.displayOrder} onChange={(e) => set('displayOrder', e.target.value)} placeholder="As the menu" />
              {err('displayOrder') && <div className="form-error-message">{err('displayOrder')}</div>}
            </div>
            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.isSoldOut} onChange={(e) => set('isSoldOut', e.target.checked)} />
                <span>Sold out here</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.isActive} onChange={(e) => set('isActive', e.target.checked)} />
                <span>On sale here</span>
              </label>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || !isDirty}
            title={!isDirty ? 'No changes to save' : undefined}>Save</button>
        </div>
      </div>
      <ConfirmDialog open={saveConfirmOpen} title="Confirm Save" message={`Save "${record.itemName}" for ${outletName}?`}
        confirmLabel="Confirm" cancelLabel="Cancel" variant="default" onConfirm={() => void handleConfirmSave()} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default DineOpsOutletItemEditModal;
